import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Page } from '../models/page.model';
import { Parcel, ParcelStatus } from '../models/parcel.model';
import { HttpWanaka } from '../utils/http.wanaka';
import { currentDateFormatted, toLocalDateString } from '../utils/utils';

import { saveAs } from 'file-saver';

export interface ParcelFilter {
  // text filters
  prefixParcel?: string;
  prefixExploitation?: string;
  prefixIdParcel?: number;

  // specy
  specyId?: number | string | number[];
  specy?: number | string | number[];

  // farm / org
  farmId?: number | string;
  farm?: number | string | number[];
  organisationId?: number | string;
  organisation?: number | string | number[];

  // campaign
  campaignId?: number | number[] | string;
  campaign?: number | number[] | string;

  // advisor
  advisorId?: number | string;
  advisor?: number | string;

  // flags / extras
  noInvoice?: boolean | string;
  completionStatus?: boolean | string;
  orderStatus?: boolean | string;
  passage?: string;
  incomplete?: boolean | string;
  tool?: string;

  postalCodeExp?: string;
  postalCodePar?: string;
  dateAvant?: string;
  dateApres?: string;
  onlyIds?: boolean | string;
  suspended?: boolean | string;
  defaultWeekIdSet?: boolean | string;
  datePlanAfter?: string;
  datePlanBefore?: string;
}

@Injectable({ providedIn: 'root' })
export class ParcelService {
  // FIXME pas en dur
  private parcelsUrl = '/parcels';

  constructor(
    private http: HttpClient,
    private httpWanaka: HttpWanaka,
  ) {}

  getParcels(): Observable<Page<Parcel>> {
    return this.http.get<any>(this.parcelsUrl).pipe(
      map((response) => response.data.map((parcel: any) => this.handleDates(parcel))),
      catchError(this.handleError),
    );
  }

  getParcel(id: number): Observable<Parcel> {
    return this.http.get<any>(this.parcelsUrl + '/' + id).pipe(
      map((response) => this.handleDates(response.data)),
      catchError(this.handleError),
    );
  }

  getParcelsAdvanced(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: ParcelFilter,
  ): Observable<Page<Parcel>> {
    let url = this.parcelsUrl + '?size=' + itemsPerPage + '&page=' + (pageIndex - 1);

    // helpers
    const has = (v: any) => v !== undefined && v !== null && v !== '';
    const append = (k: string, v: any) => {
      if (!has(v)) return;
      // arrays (e.g. campaign=[2024,2025])
      url += `&${k}=` + (Array.isArray(v) ? v.join(',') : v);
    };

    if (filter) {
      append('prefixParcel', filter.prefixParcel);
      append('prefixExploitation', filter.prefixExploitation);
      append('prefixIdParcel', filter.prefixIdParcel);

      // specy ( specyId or specy)
      append('specy', has(filter.specyId) ? filter.specyId : filter.specy);

      // farm
      append('farm', has(filter.farmId) ? filter.farmId : filter.farm);

      // campaign ( campaignId or campaign)
      append('campaign', has(filter.campaignId) ? filter.campaignId : filter.campaign);

      // organisation ( organisationId or organisation)
      append('organisation', has(filter.organisationId) ? filter.organisationId : filter.organisation);

      // advisor ( advisorId or advisor)
      append('advisor', has(filter.advisorId) ? filter.advisorId : filter.advisor);

      append('noInvoice', filter.noInvoice);
      append('completionStatus', (filter as any).completionStatus);
      append('orderStatus', (filter as any).orderStatus);
      append('passage', (filter as any).passage);

      append('postalCodeExp', (filter as any).postalCodeExp);
      append('postalCodePar', (filter as any).postalCodePar);
      append('dateAvant', (filter as any).dateAvant);
      append('dateApres', (filter as any).dateApres);
      append('onlyIds', (filter as any).onlyIds);
      append('suspended', (filter as any).suspended);
      append('defaultWeekIdSet', (filter as any).defaultWeekIdSet);
      append('datePlanAfter', (filter as any).datePlanAfter);
      append('datePlanBefore', (filter as any).datePlanBefore);
    }

    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }

    append('tool', (filter as any).tool);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((parcel: any) => this.handleDates(parcel));
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getKmls(sortColumnName: string, sortColumnOrder: string, filter: ParcelFilter) {
    let url = this.parcelsUrl + '/kml';
    let prefix = '?';

    const has = (v: any) => v !== undefined && v !== null && v !== '';
    const add = (k: string, v: any) => {
      if (!has(v)) return;
      url += `${prefix}${k}=` + (Array.isArray(v) ? v.join(',') : v);
      prefix = '&';
    };

    if (filter) {
      add('prefixParcel', filter.prefixParcel);
      add('prefixExploitation', filter.prefixExploitation);
      add('prefixIdParcel', filter.prefixIdParcel);

      add('specy', has(filter.specyId) ? filter.specyId : filter.specy);
      add('farm', has(filter.farmId) ? filter.farmId : filter.farm);

      if (has(filter.campaignId) || has(filter.campaign)) {
        add('campaign', has(filter.campaignId) ? filter.campaignId : filter.campaign);
      }

      add('organisation', has(filter.organisationId) ? filter.organisationId : filter.organisation);
      add('noInvoice', filter.noInvoice);

      add('advisor', has(filter.advisorId) ? filter.advisorId : filter.advisor);
    }

    if (sortColumnName) {
      url += `${prefix}sort=${sortColumnName},${sortColumnOrder}`;
      prefix = '&';
    }

    const formattedDate = currentDateFormatted();
    const applicationType = 'application/zip';

    this.httpWanaka.blobQuery(url, applicationType).subscribe((x) => {
      saveAs(x, 'parcels_' + formattedDate + '.zip');
    });
  }

  save(parcel: Parcel): Observable<Parcel> {
    return parcel.id ? this.put(parcel) : this.post(parcel);
  }

  delete(parcel: Parcel) {
    const url = `${this.parcelsUrl}/${parcel.id}`;
    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  getParcelStatuses(parcelIds: number[]): Observable<ParcelStatus[]> {
    const q = parcelIds && parcelIds.length ? '?parcels=' + parcelIds.join(',') : '';
    return this.http.get<any>(this.parcelsUrl + '/status/batch' + q).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  private handleDates(parcel: Parcel): Parcel {
    if (parcel.sowedOn) {
      parcel.sowedOn = new Date(parcel.sowedOn as any as string);
    }
    parcel.createdAt = new Date(parcel.createdAt as any as string);
    parcel.updatedAt = new Date(parcel.updatedAt as any as string);

    if (parcel.campaign) {
      if (parcel.campaign.startingOn) {
        parcel.campaign.startingOn = new Date(parcel.campaign.startingOn as any as string);
      }
      if (parcel.campaign.endingOn) {
        parcel.campaign.endingOn = new Date(parcel.campaign.endingOn as any as string);
      }
    }
    return parcel;
  }

  private handleError(error: any) {
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }

  private post(parcel: Parcel): Observable<Parcel> {
    return this.http.post<any>(this.parcelsUrl, this.serialize(parcel)).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private put(parcel: Parcel) {
    const url = `${this.parcelsUrl}/${parcel.id}`;
    return this.http.put<any>(url, this.serialize(parcel)).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private serialize(parcel: Parcel): any {
    return {
      ...parcel,
      sowedOn: toLocalDateString(parcel.sowedOn as any),
    };
  }
}
